import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import type {
  CompletionFinding, CompletionPlanItem, CompletionKiInfo, CompletionBaumAntwort,
  CompletionHindernisInfo, CompletionOffeneAnschluesseInfo, Sandboxmodell,
} from '../api';
import type { Rahmen } from './Projektrahmen';
import { Fehler } from './Projekte';
import { Baum } from './koordination/Baum';
import { z } from './koordination/gemeinsam';
import {
  Filterleiste, AI_COMPLETION_FILTER_LEER, type AiCompletionFilter,
} from './ai-completion/Filterleiste';

/** Projektbezogener Schlüssel für die gemerkte Modellwahl (siehe MODELL_SPEICHER unten). */
const modellSpeicherSchluessel = (projektId: string) => `completion-modell:${projektId}`;

/** Auswählbare Seitengrößen der Befundliste (Dokument 36, Abschnitt B). */
const SEITENGROESSEN = [50, 100, 250, 500] as const;

const tabs = [
  ['OPEN_FINDINGS', 'Offene Befunde'],
  ['PROPOSED', 'Vorgeschlagen'],
  ['SANDBOX_APPROVED', 'Sandbox-Warteschlange'],
  // Ein Plan, den jemand in der Werkbank am Arbeitsplatz ausgefuehrt hat.
  // Eigener Status, weil SANDBOX_APPROVED ueberall sonst heisst: es gibt einen
  // Auftrag fuer eine Revit-Maschine. Ohne diesen Reiter waeren solche Plaene
  // in der Oberflaeche gar nicht mehr auffindbar (Befund 14.09.2026).
  ['WERKBANK_FREIGEGEBEN', 'Werkbank'],
  ['RUNNING', 'Laufend'],
  ['AWAITING_ACCEPTANCE', 'Wartet auf Abnahme'],
  ['PASSED', 'Bestanden'],
  ['FAILED', 'Fehlgeschlagen'],
  ['ROLLED_BACK', 'Zurückgerollt'],
  ['MANUAL_REVIEW', 'Manuelle Prüfung'],
  ['SUPERSEDED', 'Überholt'],
] as const;

/**
 * Herkunft einer Planentscheidung (ModelActionProvenance in der Domäne).
 *
 * Die API liefert die Zahl der Enum-Ausprägung ohne Namen — Provenance trägt
 * dort keinen JsonStringEnumConverter (anders als ActionType/RiskLevel).
 * Unbekannte Zahlen bleiben roh sichtbar, statt zu verschwinden.
 */
const PROVENIENZ_NAMEN: Record<string, string> = {
  1: 'Deterministische Regel',
  2: 'Bestehende Projektregel',
  3: 'Bestehende Benutzeranweisung',
  4: 'KI-Vorschlag',
  5: 'Benutzerfreigabe',
  6: 'Revit-Validierung',
};
const provenienzName = (wert: string) => PROVENIENZ_NAMEN[wert] ?? wert;

/** Letzte 8 Zeichen einer Kennung — die volle Kennung bleibt im title-Attribut. */
const kurz = (id: string) => (id.length > 8 ? id.slice(-8) : id);

/**
 * Einstufung VOR dem Klick, als Marke (Fachplaner-Beschwerde 14.09.2026:
 * "die liste sagt nichts darüber aus, ob etwas lösbar ist oder nicht") —
 * dieselben drei Werte und dieselbe Farbgebung wie im Add-in
 * (Aussparungsfenster.Werkbank.cs, WerkbankBefundBlattZeichnen): "loesbar"
 * ist grün ("hier kommst du voran"), "unloesbar" ist die Ausnahme, bei der
 * ohne einen Menschen nichts weitergeht.
 */
const EINSTUFUNG_KLASSE: Record<string, string> = {
  loesbar: 'gut',
  unloesbar: 'schlecht',
  nicht_geplant: 'wartend',
};
const EINSTUFUNG_TEXT: Record<string, string> = {
  loesbar: 'lösbar',
  unloesbar: 'nicht lösbar',
  nicht_geplant: 'noch nicht geplant',
};
const einstufungMarke = (finding: CompletionFinding) => {
  const klasse = EINSTUFUNG_KLASSE[finding.einstufung] ?? 'wartend';
  const text = EINSTUFUNG_TEXT[finding.einstufung] ?? finding.einstufung;
  const zusatz = finding.einstufung === 'loesbar'
    ? [finding.aktionsartText, finding.vertrauenswertProzent != null ? `Vertrauenswert ${finding.vertrauenswertProzent} %` : null]
      .filter(Boolean).join(' · ')
    : finding.einstufungGrund;
  return <span className={`marke ${klasse}`} title={zusatz ?? undefined}>{text}</span>;
};

/**
 * Kontextangaben einer Vorschlagsantwort (`ki`/`hindernisse`/`offeneAnschluesse`/
 * `policy.reasons`), die nur in DIESER Sitzung entstehen — die API persistiert
 * sie (noch) nicht am Plan. Nach einem Neuladen der Seite sind sie weg; ein
 * über GET plans neu geladener Plan zeigt in diesem Abschnitt dann nur noch,
 * was `CompletionPlanItem` selbst trägt (abbruchgrund/kiAnbieter/…, siehe api.ts).
 */
interface ProposalKontext {
  ki: CompletionKiInfo | null;
  hindernisse: CompletionHindernisInfo | null;
  offeneAnschluesse: CompletionOffeneAnschluesseInfo | null;
  policyReasons: string[];
}

const proposeMeldung = (sekunden: number) =>
  `Vorschlag wird berechnet … (${sekunden} s) — Direktverbindung, dann bis zu drei KI-Versuche, bis 2 Minuten`;

/**
 * BEFUND EX-2026-00306 (13.09.2026): Ohne aktive Golden-Abnahme
 * (Completion:GoldenAcceptanceEnabled = false, der Produktionswert) trägt
 * der Plan keinen Golden-Acceptance-Report mit Downloadlink — das fertige
 * Modell liegt als gewöhnliches Job-Artefakt. Statt einer zweiten,
 * eigenen Downloadanzeige (Dublette zu Auftragsdetail.tsx) zeigt die
 * Detailkarte nur einen Verweis auf die Auftragsseite (`/auftraege/:id`),
 * die den Artefakt-Download bereits hat.
 */
export function AiCompletion() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [findings, setFindings] = useState<CompletionFinding[]>([]);
  const [gesamtanzahl, setGesamtanzahl] = useState(0);
  const [offeneAnschluesseGekappt, setOffeneAnschluesseGekappt] = useState(false);
  const [offeneAnschluesseHoechstzahl, setOffeneAnschluesseHoechstzahl] = useState<number | null>(null);
  const [offeneAnschluesseGeladenGesamt, setOffeneAnschluesseGeladenGesamt] = useState<number | null>(null);
  const [plans, setPlans] = useState<CompletionPlanItem[]>([]);
  const [tab, setTab] = useState('OPEN_FINDINGS');
  const [selected, setSelected] = useState<CompletionPlanItem | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ════════════════════════════════════════════════════════════════════
  // DOKUMENT 36, ABSCHNITT B — BAUM, FILTER UND SEITENAUFTEILUNG IN DER
  // ADRESSZEILE
  // ════════════════════════════════════════════════════════════════════
  //
  // Derselbe Aufbau wie Koordination.tsx (Batch 7): der gewählte Baumknoten
  // und die Filterleiste stehen in der URL, nicht in lokalem Zustand -
  // damit ein Arbeitsstand (Geschoss/Abschnitt/Quadrant/Medium, Befundart,
  // Planstatus, Gewerk, Nennweite, Suche, Seite) weitergegeben und
  // wiedergefunden werden kann. Die Route teilt seit dieser Änderung IMMER
  // in Seiten auf (Vorgabe Seite 1, Seitengröße 100) - ohne serverseitige
  // Filterung/Seitenaufteilung hier wäre nur die erste Seite sichtbar, ohne
  // dass es jemand merkt (siehe Klassenkommentar von ReadFindings).
  const [suchparams, setSuchparams] = useSearchParams();

  const knoten = suchparams.get('knoten');

  const filter: AiCompletionFilter = {
    befundart: suchparams.get('befundart') || AI_COMPLETION_FILTER_LEER.befundart,
    planstatus: suchparams.get('planstatus') || '',
    gewerk: suchparams.get('gewerk') || '',
    medium: suchparams.get('medium') || '',
    nennweiteVon: suchparams.get('nennweiteVon') || '',
    nennweiteBis: suchparams.get('nennweiteBis') || '',
    suche: suchparams.get('suche') || '',
  };

  const seite = Math.max(1, Number(suchparams.get('seite') || '1') || 1);
  const seitengroesseWert = Number(suchparams.get('seitengroesse') || '100');
  const seitengroesse = SEITENGROESSEN.includes(seitengroesseWert as typeof SEITENGROESSEN[number])
    ? seitengroesseWert : 100;

  const filterAendern = (f: AiCompletionFilter) => {
    const neu = new URLSearchParams(suchparams);
    const setzenOderLoeschen = (schluessel: string, wert: string) => {
      if (wert) neu.set(schluessel, wert); else neu.delete(schluessel);
    };

    setzenOderLoeschen('befundart',
      f.befundart === AI_COMPLETION_FILTER_LEER.befundart ? '' : f.befundart);
    setzenOderLoeschen('planstatus', f.planstatus);
    setzenOderLoeschen('gewerk', f.gewerk);
    setzenOderLoeschen('medium', f.medium);
    setzenOderLoeschen('nennweiteVon', f.nennweiteVon);
    setzenOderLoeschen('nennweiteBis', f.nennweiteBis);
    setzenOderLoeschen('suche', f.suche);
    neu.delete('seite');

    setSuchparams(neu, { replace: true });
  };

  const seiteAendern = (s: number) => {
    const neu = new URLSearchParams(suchparams);
    if (s > 1) neu.set('seite', String(s)); else neu.delete('seite');
    setSuchparams(neu, { replace: true });
  };

  const seitengroesseAendern = (g: number) => {
    const neu = new URLSearchParams(suchparams);
    if (g !== 100) neu.set('seitengroesse', String(g)); else neu.delete('seitengroesse');
    neu.delete('seite');
    setSuchparams(neu, { replace: true });
  };

  // Nur Blätter des Baums sind wählbar (siehe Baum.tsx) - der Schlüssel ist
  // 'geschoss|abschnitt|quadrant|medium' (CompletionEndpoints.BaumebeneGeschoss).
  const knotenWaehlen = (schluessel: string) => {
    const neu = new URLSearchParams(suchparams);
    neu.set('knoten', schluessel);
    neu.delete('seite');
    setSuchparams(neu, { replace: true });
  };

  const knotenAufheben = () => {
    const neu = new URLSearchParams(suchparams);
    neu.delete('knoten');
    neu.delete('seite');
    setSuchparams(neu, { replace: true });
  };

  const knotenTeile = knoten ? knoten.split('|') : null;
  // Der Baumknoten geht der Filterleiste vor (dasselbe Vorrangmuster wie in
  // Koordination.tsx) - wer erst einen Ast wählt und danach noch das
  // Medium-Feld tippt, würde sonst stillschweigend etwas anderes sehen als
  // den gewählten Ast.
  const geschossParam = knotenTeile?.[0] || undefined;
  const abschnittParam = knotenTeile?.[1] || undefined;
  const quadrantParam = knotenTeile?.[2] || undefined;
  const mediumParam = knotenTeile?.[3] || filter.medium || undefined;

  const [baum, setBaum] = useState<CompletionBaumAntwort | null>(null);
  const [baumLaedt, setBaumLaedt] = useState(false);

  // ════════════════════════════════════════════════════════════════════
  // DOKUMENT 36, ABSCHNITT C — VERWEIS AUS DER KOORDINATION (?planId=…)
  // ════════════════════════════════════════════════════════════════════
  //
  // Die Kollisionsliste der Koordination verweist mit dieser Adresszeile
  // auf einen bereits erzeugten Bewertungsplan. Einmalig je Parameterwert
  // angewandt (nicht bei jedem Laden erneut) - sonst könnte ein Anwender die
  // Auswahl hier nicht mehr wechseln, ohne den Parameter aus der Adresse zu
  // entfernen.
  const planIdParam = suchparams.get('planId');
  const [planIdAngewandt, setPlanIdAngewandt] = useState<string | null>(null);
  useEffect(() => {
    if (!planIdParam || planIdParam === planIdAngewandt) return;
    const treffer = plans.find((item) => item.plan.id === planIdParam);
    if (!treffer) return;
    setSelected(treffer);
    setTab(treffer.status);
    setPlanIdAngewandt(planIdParam);
  }, [planIdParam, plans, planIdAngewandt]);

  // Modellwahl über der Befundtabelle - siehe REGEL im Klassenkommentar von
  // CompletionEndpoints.ExistingFindings: die Befundliste wird JE MODELL
  // gebildet. `modelle` bleibt leer und die Auswahl damit unsichtbar, wenn
  // das Projekt (noch) keine Sandbox-Kopie hat - die Befundabfrage läuft
  // dann unverändert projektweit (alle aktiven Modelle).
  const [modelle, setModelle] = useState<Sandboxmodell[]>([]);
  const [modelleGeladen, setModelleGeladen] = useState(false);
  const [modellId, setModellIdState] = useState<string | null>(null);
  const setModellId = (id: string | null) => {
    setModellIdState(id);
    if (id) window.localStorage.setItem(modellSpeicherSchluessel(projekt.id), id);
  };

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const liste = await api.sandboxmodelle(projekt.id);
        if (abgebrochen) return;
        setModelle(liste);
        const gemerkt = window.localStorage.getItem(modellSpeicherSchluessel(projekt.id));
        setModellIdState(gemerkt && liste.some((m) => m.id === gemerkt) ? gemerkt : (liste[0]?.id ?? null));
      } catch {
        // Modellwahl bleibt leer - die Befundabfrage läuft dann projektweit,
        // wie vor dieser Erweiterung.
      } finally {
        if (!abgebrochen) setModelleGeladen(true);
      }
    })();
    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  // Fortschrittsanzeige während "Vorschlag erzeugen": die Anfrage dauert
  // 30-110 s (Direktverbindung, dann bis zu drei KI-Versuche) — ohne
  // sichtbaren Zustand wirkt die Seite in dieser Zeit wie aufgehängt.
  const [proposingId, setProposingId] = useState<string | null>(null);
  const [proposeSeconds, setProposeSeconds] = useState(0);
  const proposeTimerRef = useRef<number | null>(null);

  // Kontext je Plan-Id, nur für in dieser Sitzung erzeugte Vorschläge (siehe
  // ProposalKontext oben).
  const [proposalKontexte, setProposalKontexte] = useState<Map<string, ProposalKontext>>(new Map());

  // Die Befundabfrage braucht bei 2.950 offenen Anschlüssen 9-10 s. Ohne
  // diese Sperre läuft die nächste Umfrage schon los, während die vorige noch
  // unterwegs ist — im API-Protokoll stehen dann 499-Abbrüche.
  const ladeLaeuft = useRef(false);

  const load = async () => {
    if (ladeLaeuft.current) return;
    ladeLaeuft.current = true;
    try {
      const [f, p] = await Promise.all([
        api.completionFindings(projekt.id, {
          modelId: modellId ?? undefined,
          geschoss: geschossParam,
          abschnitt: abschnittParam,
          quadrant: quadrantParam,
          medium: mediumParam,
          gewerk: filter.gewerk || undefined,
          befundart: filter.befundart || undefined,
          planstatus: filter.planstatus || undefined,
          nennweiteVonMm: filter.nennweiteVon ? Number(filter.nennweiteVon) : undefined,
          nennweiteBisMm: filter.nennweiteBis ? Number(filter.nennweiteBis) : undefined,
          suche: filter.suche || undefined,
          seite, seitengroesse,
        }),
        api.completionPlans(projekt.id),
      ]);
      setFindings(f.findings); setPlans(p);
      setGesamtanzahl(f.gesamtanzahl);
      setOffeneAnschluesseGekappt(f.offeneAnschluesseGekappt);
      setOffeneAnschluesseHoechstzahl(f.offeneAnschluesseHoechstzahl);
      setOffeneAnschluesseGeladenGesamt(f.offeneAnschluesseGeladenGesamt);
      setSelected((current) => current
        ? p.find((item) => item.plan.id === current.plan.id) ?? current
        : null);
      setLadefehler(null);
    } catch (e) { setLadefehler(e instanceof Error ? e.message : String(e)); }
    finally { ladeLaeuft.current = false; }
  };
  useEffect(() => {
    // Wartet auf die Modellliste (modelleGeladen) - sonst liefe der erste
    // Aufruf ungefiltert und ein zweiter, gefilterter, direkt hinterher.
    if (!modelleGeladen) return;
    void load();
    const timer = window.setInterval(() => { void load(); }, 15000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projekt.id, modellId, modelleGeladen, geschossParam, abschnittParam,
      quadrantParam, mediumParam, filter.gewerk, filter.befundart, filter.planstatus,
      filter.nennweiteVon, filter.nennweiteBis, filter.suche, seite, seitengroesse]);

  // ════════════════════════════════════════════════════════════════════
  // DOKUMENT 36, ABSCHNITT B — DER BAUM (Geschoss -> Abschnitt -> Quadrant
  // -> Medium)
  // ════════════════════════════════════════════════════════════════════
  //
  // `modell` ist am Server ein PFLICHTPARAMETER (anders als bei den
  // Befunden selbst) - ohne gewähltes Sandbox-Modell lässt sich kein Baum
  // bilden (siehe CompletionEndpoints.ReadBaumAsync). Ohne Sandbox-Kopie
  // (`modelle.length === 0`) bleibt der Baum entsprechend leer, die
  // Befundliste läuft wie zuvor projektweit weiter.
  useEffect(() => {
    if (!modelleGeladen || !modellId) { setBaum(null); return; }

    setBaumLaedt(true);
    api.completionBaum(projekt.id, modellId,
      filter.befundart as 'alle' | 'anschluss' | 'kollision')
      .then((r) => { setBaum(r); setBaumLaedt(false); })
      .catch((e) => {
        setLadefehler(e instanceof Error ? e.message : String(e));
        setBaumLaedt(false);
      });
  }, [api, projekt.id, modellId, modelleGeladen, filter.befundart]);

  // Ueberholte und abgelehnte Plaene blenden den Befund NICHT mehr aus
  // "Offene Befunde" aus - siehe CompletionPlanAblage.PutAsync: ein neuer
  // Vorschlag fuer denselben Befund ueberholt den alten, der Befund soll
  // dann wieder als offen erscheinen, bis der neue Plan entschieden ist.
  const existing = useMemo(() => new Set(
    plans.filter((p) => p.status !== 'SUPERSEDED' && p.status !== 'REJECTED')
      .map((p) => p.plan.findingId),
  ), [plans]);
  const selectedFinding = useMemo(
    () => selected ? findings.find((finding) => finding.id === selected.plan.findingId) : undefined,
    [findings, selected],
  );
  // 'findings' ist bereits die vom Server gefilterte und seitenweise
  // geschnittene Menge (Baum + Filterleiste, Dokument 36 Abschnitt B) - die
  // frühere lokale Textsuche über 'filterText' entfiel dafür bewusst: bei
  // serverseitiger Seitenaufteilung sieht eine Suche im Browser nur die
  // geladene Seite, nicht den ganzen Bestand (siehe Filterleiste 'suche').
  const offeneFindings = tab === 'OPEN_FINDINGS'
    ? findings.filter((f) => !existing.has(f.id)) : [];
  const ausgeblendetMitPlan = findings.length - offeneFindings.length;
  const visiblePlans = plans.filter((p) => tab !== 'OPEN_FINDINGS' && p.status === tab);

  // ════════════════════════════════════════════════════════════════════
  // DOKUMENT 36, ABSCHNITT B — SEITENAUFTEILUNG SICHTBAR MACHEN
  // ════════════════════════════════════════════════════════════════════
  const gesamtseiten = Math.max(1, Math.ceil(gesamtanzahl / Math.max(1, seitengroesse)));
  const zeileVon = gesamtanzahl === 0 ? 0 : (seite - 1) * seitengroesse + 1;
  const zeileBis = Math.min(seite * seitengroesse, gesamtanzahl);

  const propose = async (findingId: string) => {
    setBusy(true);
    setAktionsfehler(null);
    setProposingId(findingId);
    setProposeSeconds(0);
    proposeTimerRef.current = window.setInterval(() => {
      setProposeSeconds((sekunden) => sekunden + 1);
    }, 1000);
    try {
      const result = await api.completionProposal(projekt.id, findingId);
      setSelected(result.item);
      setProposalKontexte((bisher) => {
        const naechste = new Map(bisher);
        naechste.set(result.item.plan.id, {
          ki: result.ki, hindernisse: result.hindernisse,
          offeneAnschluesse: result.offeneAnschluesse, policyReasons: result.policy.reasons,
        });
        return naechste;
      });
      await load();
      setTab(result.item.status);
    } catch (e) { setAktionsfehler(e instanceof Error ? e.message : String(e)); }
    finally {
      if (proposeTimerRef.current != null) { window.clearInterval(proposeTimerRef.current); proposeTimerRef.current = null; }
      setProposingId(null);
      setBusy(false);
    }
  };

  const change = async (action: 'approve-sandbox' | 'reject' | 'retry' | 'manual') => {
    if (!selected) return;
    setBusy(true);
    setAktionsfehler(null);
    try { const result = await api.completionStatus(projekt.id, selected.plan.id, action); setSelected('item' in result ? result.item : result); await load(); }
    catch (e) { setAktionsfehler(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  // Ein ManualReviewRequired-Plan (Confidence 0, kein Entwurf hat die
  // Routenprüfung bestanden) wird von der API bei approve-sandbox immer mit
  // 409 abgewiesen — der Knopf zeigt das vorher an, statt es abzuwarten.
  const nichtFreigabefaehig = selected?.plan.actionType === 'ManualReviewRequired';

  // KI-Bewertung einer bestehenden Kollision (siehe KollisionsBewerter.cs) —
  // ein Vorschlag zur MANUELLEN Umsetzung, keine Geometrie. ClashAssessment
  // steht bewusst nicht auf der Schreib-Whitelist (CompletionPolicy) — die
  // API lehnt "Sandbox freigeben" für diesen Plan immer mit 409 ab, der
  // Knopf zeigt das hier ebenfalls vorher an.
  const istBewertungsplan = selected?.plan.actionType === 'ClashAssessment';
  const bewertungExt: Record<string, string | null> = istBewertungsplan
    ? selected?.plan.operations[0]?.parameters?.extensions ?? {}
    : {};
  const bewertungPruefpunkte = (() => {
    try { return JSON.parse(bewertungExt.pruefpunkteJson ?? '[]') as string[]; }
    catch { return []; }
  })();

  // Ueberholt (CompletionPlanAblage.PutAsync, 'completion-freigeben' lehnt
  // ihn serverseitig ohnehin ab): alle Aktionen ausblenden, statt einen
  // 409/Exit-1-Fehler erst nach dem Klick zu zeigen.
  const ueberholt = selected?.status === 'SUPERSEDED';

  const kontext = selected ? proposalKontexte.get(selected.plan.id) : undefined;
  const abbruchgrund = kontext?.ki?.abbruchgrund ?? selected?.abbruchgrund ?? null;
  const kiAnbieter = kontext?.ki?.anbieter ?? selected?.kiAnbieter ?? null;
  const kiModell = kontext?.ki?.modell ?? selected?.kiModell ?? null;
  const kiVersucheZahl = kontext?.ki?.versuche ?? selected?.kiVersuche ?? null;
  const hindernisse = kontext?.hindernisse ?? null;
  const offeneAnschluesse = kontext?.offeneAnschluesse ?? null;
  const policyReasons = kontext?.policyReasons ?? [];
  const warumSichtbar = Boolean(abbruchgrund || kiAnbieter || kiModell || kiVersucheZahl != null
    || hindernisse || offeneAnschluesse || policyReasons.length > 0);

  return <div className="karte">
    <h2 className="karte-titel">KI-Modellvervollständigung</h2>
    <p className="schwach">Bestehende Befunde, kontrollierte Sandbox-Vorschläge und validierte Worker-Ergebnisse.</p>
    {ladefehler && <Fehler text={ladefehler} />}
    <div className="reiter" role="tablist">
      {tabs.map(([key, label]) => <button key={key} className={`knopf ${tab === key ? 'aktiv' : ''}`} onClick={() => setTab(key)}>{label}</button>)}
    </div>
    {tab === 'OPEN_FINDINGS' && modelle.length > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0' }}>
      <label className="schwach" htmlFor="completion-modellwahl">Modell:</label>
      <select id="completion-modellwahl" value={modellId ?? ''} onChange={(e) => setModellId(e.target.value || null)}>
        {modelle.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>}

    {/* ── Dokument 36, Abschnitt B: links der Baum (Geschoss -> Abschnitt ->
        Quadrant -> Medium), rechts Filterleiste + seitenweise Liste — das
        Muster aus Koordination.tsx, wiederverwendet über koordination/Baum.tsx
        und ein eigenes ai-completion/Filterleiste.tsx (andere Felder: dort
        Lösungsart/Korb, hier Befundart/Planstatus/Nennweite). */}
    {tab === 'OPEN_FINDINGS' ? (
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <Baum
            baum={baum?.wurzel ?? null}
            gewaehlt={knoten}
            onWaehlen={(schluessel) => knotenWaehlen(schluessel)}
            laedt={baumLaedt}
            leerHinweis={modellId
              ? 'Kein Baum geladen — noch kein Koordinationsstand für dieses Modell?'
              : 'Kein Sandbox-Modell gewählt — der Baum braucht eine Modellwahl.'}
          />
          {knoten && <button type="button" className="knopf"
            style={{ marginTop: 8, fontSize: 11, width: '100%' }}
            onClick={knotenAufheben}>
            Baumauswahl aufheben ({knoten.split('|').join(' / ')})
          </button>}
        </div>

        <div style={{ flex: '1 1 640px', minWidth: 320 }}>
          <Filterleiste filter={filter} onChange={filterAendern}
            gezeigt={findings.length} gesamt={gesamtanzahl} />

          {offeneAnschluesseGekappt && <div className="hinweis warnung" style={{ margin: '10px 0' }}>
            Liste gekappt: {offeneAnschluesseGeladenGesamt} offene Anschlüsse gefunden, geladen sind nur die ersten {offeneAnschluesseHoechstzahl}.
          </div>}
          {proposingId && <p className="schwach" style={{ margin: '10px 0' }}>
            {proposeMeldung(proposeSeconds)}
          </p>}
          {ausgeblendetMitPlan > 0 && <p className="schwach" style={{ margin: '10px 0' }}>
            {ausgeblendetMitPlan} von {findings.length} Befunden dieser Seite bereits mit aktivem Plan ausgeblendet (siehe die übrigen Reiter).
          </p>}

          <div className="tabellenrahmen">
            <table><thead><tr>
              <th>Befund-Id</th><th>Typ</th><th>Befund</th><th>Element</th><th>Connector</th><th>DN</th>
              <th>System</th><th>Geschoss</th><th>Abschnitt</th><th>Quadrant</th><th>Gewerk</th><th>Medium</th>
              <th>Achsadresse</th><th>Zuordnung</th><th>Einstufung</th><th>Planstatus</th><th />
            </tr></thead><tbody>
              {offeneFindings.map((finding) => <tr key={finding.id}>
                <td title={finding.id}>{kurz(finding.id)}</td>
                <td>{finding.findingType}</td>
                <td><strong>{finding.summary}</strong><br /><span className="schwach">{finding.details}</span></td>
                <td title={finding.componentUniqueId ?? undefined}>{finding.componentUniqueId ? kurz(finding.componentUniqueId) : '—'}</td>
                <td>{finding.openConnector?.connectorId ?? '—'}</td>
                <td>{finding.openConnector?.nominalDiameterMm != null ? `DN ${finding.openConnector.nominalDiameterMm}` : '—'}</td>
                <td>{finding.system ?? '—'}</td>
                <td>{finding.level ?? '—'}</td>
                <td>{finding.abschnitt ?? '—'}</td>
                <td>{finding.quadrant ?? '—'}</td>
                <td>{finding.gewerk ?? '—'}</td>
                <td>{finding.medium ?? '—'}</td>
                <td>{finding.achsadresseText ?? '—'}</td>
                <td>{finding.zugeordnet
                  ? '—'
                  : <span className="marke warnung">ohne Zuordnung: {finding.zugeordnetGrund ?? 'kein Grund angegeben'}</span>}</td>
                <td>{einstufungMarke(finding)}</td>
                <td>{finding.planStatus}</td>
                <td>
                  <button className="knopf" disabled={busy} onClick={() => propose(finding.id)}>{proposingId === finding.id ? 'läuft …' : 'Vorschlag erzeugen'}</button>
                  {proposingId === finding.id && <div className="schwach" style={{ fontSize: 11, marginTop: 4 }}>{proposeSeconds} s</div>}
                </td>
              </tr>)}
              {offeneFindings.length === 0 && <tr>
                <td colSpan={17} className="schwach">Keine Befunde für diese Auswahl.</td>
              </tr>}
            </tbody></table>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="knopf" disabled={seite <= 1}
              onClick={() => seiteAendern(seite - 1)}>← Zurück</button>
            <span className="schwach" style={{ fontSize: 12 }}>
              {gesamtanzahl === 0
                ? 'Keine Treffer'
                : `Zeile ${z(zeileVon)} bis ${z(zeileBis)} von ${z(gesamtanzahl)}`}
              {' '}(Seite {seite} von {gesamtseiten})
            </span>
            <button type="button" className="knopf" disabled={seite >= gesamtseiten}
              onClick={() => seiteAendern(seite + 1)}>Weiter →</button>
            <label className="schwach" style={{ fontSize: 12, marginLeft: 'auto' }}>
              Zeilen je Seite:{' '}
              <select value={seitengroesse}
                onChange={(e) => seitengroesseAendern(Number(e.target.value))}>
                {SEITENGROESSEN.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
          </div>
        </div>
      </div>
    ) : <table><thead><tr><th>Status</th><th>Aktion</th><th>Vertrauen</th><th>Risiko</th><th>Modellstand</th></tr></thead><tbody>
      {visiblePlans.map((item) => <tr key={item.plan.id} className="klickbar" onClick={() => setSelected(item)}><td>{item.status}</td><td>{item.plan.actionType === 'ClashAssessment' ? 'KI-Bewertung' : item.plan.actionType}</td><td>{Math.round(item.plan.confidence * 100)} %</td><td>{item.plan.riskLevel}</td><td>{item.plan.modelVersion}</td></tr>)}
    </tbody></table>}
    {selected && <section className="karte" style={{ marginTop: 18 }}><h3>Plan im Detail</h3><p><strong>Status:</strong> {selected.status} · <strong>Aktualisiert:</strong> {selected.updatedAt}</p>{selected.runtime && <><p><strong>Worker:</strong> {selected.runtime.jobNumber ?? selected.runtime.jobId} · {selected.runtime.jobStatus}{selected.runtime.failureReason ? ` · ${selected.runtime.failureReason}` : ''} · <Link to={`/auftraege/${selected.runtime.jobId}`}>Auftrag öffnen (Artefakt-Download)</Link></p>{selected.runtime.acceptance && <div><p><strong>Golden RVT Acceptance:</strong> {selected.runtime.acceptance.status} · {selected.runtime.acceptance.caseId} ({selected.runtime.acceptance.caseType}){selected.runtime.acceptance.evaluatedAt ? ` · ${selected.runtime.acceptance.evaluatedAt}` : ''}</p>{selected.runtime.acceptance.reasons.length > 0 ? <ul>{selected.runtime.acceptance.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul> : <p className="schwach">Alle Pflichtvalidierungen bestanden.</p>}{selected.runtime.acceptance.reportSha256 && <p className="schwach">Acceptance Report SHA-256: {selected.runtime.acceptance.reportSha256}</p>}</div>}{selected.runtime.issues.length > 0 && <ul>{selected.runtime.issues.map((issue) => <li key={`${issue.createdAt}-${issue.code}`}><strong>{issue.level} / {issue.code}:</strong> {issue.message}</li>)}</ul>}</>}<p><strong>Finding:</strong> {selected.plan.findingId ?? '—'} · <strong>Issue:</strong> {selected.plan.issueId ?? '—'}</p>{selectedFinding && <p><strong>Befund:</strong> {selectedFinding.findingType} · {selectedFinding.summary}<br />{selectedFinding.details}<br /><strong>Element:</strong> {selectedFinding.componentUniqueId ?? '—'} · <strong>System:</strong> {selectedFinding.system ?? '—'} / {selectedFinding.systemType ?? '—'} · <strong>Raum/Ebene:</strong> {selectedFinding.room ?? '—'} / {selectedFinding.level ?? '—'}<br /><strong>Offener Connector:</strong> {selectedFinding.openConnector ? `${selectedFinding.openConnector.elementUniqueId}/${selectedFinding.openConnector.connectorId}` : '—'} · <strong>Zielnetz:</strong> {selectedFinding.nearbyNetwork ?? '—'}<br /><strong>Quelle:</strong> {selectedFinding.sourceModel ?? '—'} @ {selectedFinding.sourceModelVersion ?? '—'}</p>}<p><strong>Model:</strong> {selected.plan.modelId} · <strong>Version:</strong> {selected.plan.modelVersion} · <strong>Erstellt:</strong> {selected.plan.createdAt ?? '—'}</p><p><strong>Plan:</strong> {selected.plan.actionType} · {Math.round(selected.plan.confidence * 100)} % · {selected.plan.riskLevel} · {selected.plan.approvalMode} · {selected.plan.autonomyLevel}</p><ul>{selected.plan.operations.map((operation) => <li key={operation.operationId}>{operation.actionType}: {operation.reason ?? '—'} ({operation.targetElements.join(', ')})</li>)}</ul><p><strong>Provenienz:</strong> {(selected.plan.provenance ?? []).map((wert) => provenienzName(String(wert))).join(', ') || '—'} · <strong>Rollback:</strong> {selected.plan.rollbackStrategy?.kind ?? '—'}</p>

      {istBewertungsplan && <div style={{ marginTop: 14 }}>
        <h4>KI-Bewertung der Kollision</h4>
        <p><strong>Bewertung:</strong> {bewertungExt.bewertung ?? '—'} · <strong>Risiko:</strong> {bewertungExt.risiko ?? selected.plan.riskLevel} · <strong>Zuversicht:</strong> {bewertungExt.zuversicht ?? Math.round(selected.plan.confidence * 100) / 100}</p>
        <p><strong>Maßnahme:</strong> {bewertungExt.massnahme ?? '—'}</p>
        <p><strong>Weicher:</strong> {bewertungExt.weicher ?? '—'}{bewertungExt.weicherUniqueId ? ` (${kurz(bewertungExt.weicherUniqueId)})` : ''} · <strong>Richtung:</strong> {bewertungExt.richtung ?? '—'} · <strong>Betrag:</strong> {bewertungExt.betragMm != null ? `${bewertungExt.betragMm} mm` : '—'}</p>
        <p><strong>Begründung:</strong> {bewertungExt.begruendung ?? '—'}</p>
        {bewertungPruefpunkte.length > 0 && <div><strong>Prüfpunkte:</strong><ul>{bewertungPruefpunkte.map((punkt) => <li key={punkt}>{punkt}</li>)}</ul></div>}
        <p className="schwach">Bewertungsplan zur manuellen Umsetzung — keine Sandbox-Ausführung.</p>
      </div>}

      {warumSichtbar && <div style={{ marginTop: 14 }}>
        <h4>Warum dieses Ergebnis</h4>
        {abbruchgrund && <p><strong>Abbruchgrund: {abbruchgrund}</strong></p>}
        {(kiAnbieter || kiModell || kiVersucheZahl != null) && <p><strong>KI:</strong> {kiAnbieter ?? '—'} / {kiModell ?? '—'} · {kiVersucheZahl ?? '—'} Versuch(e)</p>}
        {hindernisse && <p>
          <strong>Hindernisse:</strong> geladen {hindernisse.geladen} · gefunden im Suchradius ({hindernisse.suchradiusMm} mm) {hindernisse.gefundenImSuchradius ?? '—'} · zusätzlich entlang erwarteter Route +{hindernisse.zusaetzlichEntlangErwarteterRoute}
          {hindernisse.gedeckelt && <span className="marke warnung" style={{ marginLeft: 6 }}>gedeckelt</span>}
          {hindernisse.zusaetzlichEntlangErwarteterRouteGedeckelt && <span className="marke warnung" style={{ marginLeft: 6 }}>Routen-Ergänzung gedeckelt</span>}
        </p>}
        {(hindernisse?.gedeckelt || hindernisse?.zusaetzlichEntlangErwarteterRouteGedeckelt) && <div className="hinweis warnung">
          Kontext gekappt — Prüfung sieht nur die nächsten {hindernisse?.geladen} Hindernisse.
        </div>}
        {offeneAnschluesse && <p>
          <strong>Offene Anschlüsse:</strong> geladen {offeneAnschluesse.geladen} · gefunden im Modell {offeneAnschluesse.gefundenImModell ?? '—'} (Höchstzahl {offeneAnschluesse.hoechstzahl})
          {offeneAnschluesse.gedeckelt && <span className="marke warnung" style={{ marginLeft: 6 }}>gedeckelt</span>}
        </p>}
        {policyReasons.length > 0 && <div><strong>Policy-Gründe:</strong><ul>{policyReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div>}
      </div>}

      {selected.versuche && selected.versuche.length > 0 && <div style={{ marginTop: 14 }}>
        <h4>Versuchsverlauf</h4>
        {selected.versuche.map((versuch) => <details key={versuch.nummer} style={{ marginBottom: 8 }}>
          <summary>Versuch {versuch.nummer} — {versuch.herkunft === 'direkt' ? 'Direktverbindung' : 'KI'} · {versuch.aktionsart}</summary>
          <div style={{ padding: '8px 0 8px 12px' }}>
            {versuch.wegpunkte != null && <p className="schwach">Wegpunkte: {versuch.wegpunkte}</p>}
            {versuch.route.length > 0 && <table><thead><tr><th>Kennzeichen</th><th>X</th><th>Y</th><th>Z</th></tr></thead><tbody>
              {versuch.route.map((punkt, i) => <tr key={i}><td>{punkt.kennzeichen}</td><td>{punkt.x}</td><td>{punkt.y}</td><td>{punkt.z}</td></tr>)}
            </tbody></table>}
            {versuch.bestanden.length > 0 && <p>{versuch.bestanden.map((code) => <span key={code} className="marke gut" style={{ marginRight: 4 }}>{code}</span>)}</p>}
            {versuch.fehlschlaege.map((fehlschlag, fi) => <div key={fi} style={{ marginTop: 6 }}>
              <p><span className="marke schlecht" style={{ marginRight: 6 }}>{fehlschlag.code}</span>{fehlschlag.meldung}</p>
              {fehlschlag.treffer.length > 0 && <table><thead><tr><th>Hindernis</th><th>Segment</th><th>Eindringtiefe X</th><th>Eindringtiefe Y</th><th>Eindringtiefe Z</th></tr></thead><tbody>
                {fehlschlag.treffer.map((treffer, ti) => <tr key={ti}><td title={treffer.hindernisUniqueId}>{kurz(treffer.hindernisUniqueId)}</td><td>{treffer.segment}</td><td>{treffer.eindringtiefeX}</td><td>{treffer.eindringtiefeY}</td><td>{treffer.eindringtiefeZ}</td></tr>)}
              </tbody></table>}
            </div>)}
            {(versuch.hindernisseNachgeladen != null || versuch.hindernisseNeuErgaenzt != null) && <p className="schwach">Hindernisse nachgeladen: {versuch.hindernisseNachgeladen ?? '—'} · neu ergänzt: {versuch.hindernisseNeuErgaenzt ?? '—'}</p>}
            {versuch.fehlervermerk && <p className="schwach">{versuch.fehlervermerk}</p>}
          </div>
        </details>)}
      </div>}

      {aktionsfehler && <Fehler text={aktionsfehler} />}{ueberholt && <p className="schwach">Überholt durch einen neueren Vorschlag.</p>}<div className="aktionen"><button className="knopf" disabled={busy || nichtFreigabefaehig || istBewertungsplan || ueberholt} title={istBewertungsplan ? 'Bewertungsplan zur manuellen Umsetzung' : undefined} onClick={() => change('approve-sandbox')}>Sandbox freigeben</button><button className="knopf" disabled={busy || ueberholt} onClick={() => change('reject')}>Ablehnen</button><button className="knopf" disabled={busy || ueberholt} onClick={() => change('retry')}>Erneut</button><button className="knopf" disabled={busy || ueberholt} onClick={() => change('manual')}>Manuell</button></div>{nichtFreigabefaehig && !ueberholt && <p className="schwach">Kein Entwurf hat die Routenprüfung bestanden — nicht freigabefähig. Ablehnen oder Manuell.</p>}{istBewertungsplan && !ueberholt && <p className="schwach">Bewertungsplan zur manuellen Umsetzung — keine Sandbox-Ausführung.</p>}</section>}
  </div>;
}
